/* Pending Parcels — service-wise report (ITPS/EMS/CMS split USA vs non-USA). */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ArrowUpRight, Copy, ExternalLink, Layers, Loader2 } from "lucide-react";
import { ParcelNoteBadge, HL } from "@/components/ParcelNotes";
import { openParcelQuery } from "@/components/ParcelQueryDialog";
import { NoteHover, QueryHover } from "@/components/PendingHover";
import { API, getAuthHeader } from "@/App";
import { OutsourcedTag } from "@/components/OutsourcedTag";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const slug = (b) => b.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const BucketCard = ({ b, onOpen }) => (
  <button type="button" onClick={() => onOpen(b.bucket)} data-testid={`psr-bucket-${slug(b.bucket)}`}
    className={`text-left rounded-lg border p-3 transition-colors hover:shadow-sm ${b.usa_docs
      ? "border-indigo-300 bg-indigo-50 hover:bg-indigo-100" : "border-slate-200 bg-white hover:bg-slate-50"}`}>
    <div className="flex items-center justify-between gap-2">
      <span className="text-xs font-bold tracking-wide text-slate-800">{b.bucket}</span>
      {b.usa_docs && <span className="text-[9px] font-bold uppercase rounded bg-indigo-600 px-1.5 py-0.5 text-white">USA docs</span>}
    </div>
    <div className="mt-1 text-2xl font-bold text-slate-900" data-testid={`psr-count-${slug(b.bucket)}`}>{b.parcels}</div>
    <div className="text-[11px] text-slate-500">{b.rows} rows · oldest {b.oldest_age_days ?? "—"}d</div>
    <div className="mt-1 text-[11px] font-semibold text-red-600" data-testid={`psr-over48-${slug(b.bucket)}`}>{b.over_48h} over 48h</div>
  </button>
);

const me = () => { try { return JSON.parse(localStorage.getItem("user") || "{}"); } catch { return {}; } };
const cellBg = (p, f) => { const c = p.pnote?.cell_hl?.[f]; return c ? `or-hl-cell or-hl-${c}` : ""; };
const copyText = async (t) => {
  try { await navigator.clipboard.writeText(t); }
  catch {
    const el = Object.assign(document.createElement("textarea"), { value: t });
    document.body.appendChild(el); el.select(); document.execCommand("copy"); el.remove();
  }
  toast.success(`Copied ${t}`);
};
export const openWorklist = (bucket, f) => {
  const p = new URLSearchParams({ bucket });
  if (f) { p.set("age", f.age || "total"); if (f.user) p.set("user", f.user); if (f.label) p.set("who", f.label); }
  window.open(`/pending-worklist?${p.toString()}`, "_blank", "noopener");
};

export const QUERY_FILTERS = [["", "All queries"], ["any", "Any query"], ["stale", "No reply 24h+"], ["open", "Open"], ["solved", "Solved (to process)"],
  ["processed", "Processed"], ["none", "No query"]];
export const queryMatch = (p, f) => !f || (f === "any" ? !!p.pquery : f === "none" ? !p.pquery
  : f === "stale" ? !!p.pquery?.stale : p.pquery?.status === f);
/* Apply the query filter; "No reply 24h+" lists the longest-silent queries first. */
export const applyQueryFilter = (list, f) => {
  const out = list.filter((p) => queryMatch(p, f));
  return f === "stale" ? [...out].sort((a, b) => String(a.pquery.last_activity).localeCompare(String(b.pquery.last_activity))) : out;
};
export const QueryFilterSelect = ({ value, onChange, parcels, testId }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
    className={`h-8 rounded border px-2 text-xs ${value ? "border-orange-400 bg-orange-50 font-semibold text-orange-800" : ""}`}>
    {QUERY_FILTERS.map(([v, l]) => <option key={v} value={v} data-testid={`${testId}-opt-${v || "all"}`}>
      {parcels ? `${l} (${parcels.filter((p) => queryMatch(p, v)).length})` : l}</option>)}
  </select>);

/* Reason filter: All / With any reason / No reason + every reason present (with counts). */
export const reasonMatch = (p, f) => !f || (f === "__any" ? !!(p.reason || "").trim() : f === "__none" ? !(p.reason || "").trim()
  : (p.reason || "").trim() === f || (p.products || []).some(x => x.issue === f));
export const ReasonFilterSelect = ({ value, onChange, parcels, testId }) => {
  const list = parcels || [];
  const reasons = [...new Set(list.flatMap(p => [(p.reason || "").trim(), ...(p.products || []).map(x => x.issue || "")])
    .filter(Boolean))].sort();
  const n = (f) => (parcels ? ` (${list.filter(p => reasonMatch(p, f)).length})` : "");
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
      className={`h-8 max-w-[220px] rounded border px-2 text-xs ${value ? "border-sky-400 bg-sky-50 font-semibold text-sky-800" : ""}`}>
      <option value="">All reasons{n("")}</option>
      <option value="__any">With any reason{n("__any")}</option>
      <option value="__none">No reason{n("__none")}</option>
      {reasons.map(r => <option key={r} value={r}>{r}{n(r)}</option>)}
    </select>);
};

const Q_CLS = { open: "bg-orange-500", solved: "bg-emerald-600", processed: "bg-blue-600" };
const QueryCell = ({ p, i }) => (
  <button type="button" onClick={(e) => { e.stopPropagation(); openParcelQuery(p.first_id); }} onDoubleClick={(e) => e.stopPropagation()}
    data-testid={`psr-query-${i}`} title={p.pquery ? `${p.pquery.type}${p.pquery.outcome ? ` → ${p.pquery.outcome}` : ""}${p.pquery.stale ? ` · no reply since ${new Date(p.pquery.last_activity).toLocaleString()}` : ""}` : "Raise a query on this parcel"}
    className={p.pquery ? `rounded px-1.5 py-0.5 text-[9px] font-bold uppercase text-white ${p.pquery.stale ? "bg-rose-700 animate-pulse" : Q_CLS[p.pquery.status] || "bg-slate-500"}`
      : "rounded border border-dashed border-orange-300 px-1.5 py-0.5 text-[10px] font-semibold text-orange-600 hover:bg-orange-50"}>
    {p.pquery ? `${p.pquery.stale ? "No reply 24h+" : p.pquery.status}${p.pquery.count > 1 ? ` ·${p.pquery.count}` : ""}` : "+ Query"}
  </button>);

/* Phone card for one pending parcel (same data and actions as a table row). */
const ParcelCard = ({ p, i, ro, u, onChanged, onRowClick, showService }) => (
  <div className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-[11px]" data-testid={`psr-card-${i}`}
    style={p.pnote?.row_hl ? { backgroundColor: HL[p.pnote.row_hl].bg } : undefined}>
    <div className="flex items-start gap-2">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-slate-900 truncate">{p.customer_name || "—"}</div>
        <div className="text-slate-500 truncate">{p.code} · {p.date} · {p.country || "—"}</div>
      </div>
      <div className="flex flex-wrap justify-end gap-1 shrink-0">
        {showService && <span className="rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-700">{p.bucket}</span>}
        <OutsourcedTag mode={p.bucket} testid={`psr-card-outsourced-${i}`} />
        <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${p.over_48h ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600"}`}>{p.age_days ?? "—"}d</span>
      </div>
    </div>
    <div className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-0.5">
      <div className="min-w-0 col-span-2 flex items-center gap-1"><span className="text-slate-400">Tracking</span>
        <span className="font-mono font-medium text-slate-700 break-all">{p.tracking || "—"}</span>
        {p.tracking && <button type="button" onClick={() => copyText(p.tracking)} data-testid={`psr-card-copy-${i}`}
          className="rounded p-1 text-slate-500 active:bg-indigo-50"><Copy className="w-3.5 h-3.5" /></button>}</div>
      <div className="min-w-0"><span className="text-slate-400">Invoice </span><span className="font-semibold text-slate-800">{p.invoice_no || "—"}</span></div>
      {(p.reason_detail || p.reason) && <div className="min-w-0 col-span-2 text-rose-700">Reason: {p.reason_detail || p.reason}</div>}
    </div>
    <div className="mt-1 border-t border-slate-100 pt-1 space-y-0.5" data-testid={`psr-card-products-${i}`}>
      {p.products.map((x, k) => (
        <div key={k} className={`flex gap-2 ${x.issue ? "text-rose-700 font-semibold" : "text-slate-700"}`}>
          <span className="flex-1 min-w-0 break-words">{x.issue ? "⚠ " : ""}{x.medicine}{x.issue ? <i className="font-normal"> ({x.issue})</i> : null}</span>
          <span className="font-bold">×{x.quantity}</span></div>))}
    </div>
    {p.pnote?.last && <div className="mt-1 rounded bg-yellow-50 px-1.5 py-1 text-slate-700 break-words"
      style={p.pnote.last.hl ? { backgroundColor: HL[p.pnote.last.hl].bg } : undefined}><b>{p.pnote.last.by_name}:</b> {p.pnote.last.text}</div>}
    <div className="mt-1.5 flex items-center gap-2">
      {p.first_id && <ParcelNoteBadge row={{ id: p.first_id, customer_name: p.customer_name, pnote: p.pnote }}
        readOnly={ro} isAdmin={u.role === "admin"} me={u.id} onChanged={onChanged} source="pending_parcels" />}
      {p.first_id && !(ro && !p.pquery) && <QueryCell p={p} i={`card-${i}`} />}
      {onRowClick && <button type="button" onClick={() => onRowClick(p)} data-testid={`psr-card-open-${i}`}
        className="ml-auto inline-flex items-center gap-1 rounded-full border border-indigo-300 px-2.5 py-1 font-semibold text-indigo-700 active:bg-indigo-50">
        <ExternalLink className="w-3.5 h-3.5" /> Open</button>}
    </div>
  </div>
);

const CARD_PAGE = 40;
export const ParcelTable = ({ parcels, onChanged, onRowClick, tall, showService }) => {
  const u = me();
  const [cardN, setCardN] = useState(CARD_PAGE);
  const ro = u.role !== "admin" && ((u.order_register_access === "viewer" && !u.is_query_team)
    || (u.panel_rights || {}).pending_parcels === "view");
  useEffect(() => {
    const h = () => onChanged?.();
    window.addEventListener("pq-changed", h);
    return () => window.removeEventListener("pq-changed", h);
  }, [onChanged]);
  return (<>
  <div className="md:hidden space-y-2" data-testid="psr-cards">
    {parcels.slice(0, cardN).map((p, i) => <ParcelCard key={`${p.tracking}-${p.code}-${i}`} p={p} i={i} ro={ro} u={u} onChanged={onChanged} onRowClick={onRowClick} showService={showService} />)}
    {parcels.length > cardN && <button type="button" onClick={() => setCardN((n) => n + CARD_PAGE)} data-testid="psr-cards-more"
      className="w-full rounded-lg border border-indigo-300 bg-white py-2 text-xs font-semibold text-indigo-700 active:bg-indigo-50">
      Show more ({parcels.length - cardN} left)</button>}
    {!parcels.length && <p className="py-6 text-center text-sm text-slate-500">No parcels.</p>}
  </div>
  <div className={`${tall ? "max-h-[calc(100vh-180px)]" : "max-h-[60vh]"} overflow-auto rounded border hidden md:block`} data-testid="psr-table">
    <table className="w-full text-[11px]">
      <thead className="sticky top-0 z-10 bg-slate-100 text-slate-600">
        <tr>{["#", ...(showService ? ["Service"] : []), "Order date", "Age", "Code", "Customer", "Country", "Tracking", "Invoice", "Products", "Reason", "Team note", "Query"]
          .map(h => <th key={h} className="px-2 py-1.5 text-left font-semibold whitespace-nowrap">{h}</th>)}</tr>
      </thead>
      <tbody>
        {parcels.map((p, i) => (
          <tr key={`${p.tracking}-${p.code}-${i}`} data-testid={`psr-row-${i}`}
            className={`border-t hover:bg-slate-50 ${onRowClick ? "cursor-pointer" : ""}`}
            style={p.pnote?.row_hl ? { backgroundColor: HL[p.pnote.row_hl].bg } : undefined}
            title={onRowClick ? "Double-click to open in the Order Register (new tab)" : undefined}
            onDoubleClick={onRowClick ? () => { window.getSelection()?.removeAllRanges(); onRowClick(p); } : undefined}>
            <td className="px-2 py-1 text-slate-400">{i + 1}</td>
            {showService && <td className="px-2 py-1 whitespace-nowrap font-semibold text-indigo-700" data-testid={`psr-svc-${i}`}>{p.bucket}<OutsourcedTag mode={p.bucket} testid={`psr-outsourced-${i}`} /></td>}
            <td className="px-2 py-1 whitespace-nowrap">{p.date}</td>
            <td className={`px-2 py-1 font-semibold ${p.over_48h ? "text-red-600" : "text-slate-600"}`}>{p.age_days ?? "—"}d</td>
            <td className={`px-2 py-1 font-mono ${cellBg(p, "code")}`}>{p.code}</td>
            <td className={`px-2 py-1 ${cellBg(p, "customer_name")}`}>{p.customer_name}</td>
            <td className={`px-2 py-1 ${cellBg(p, "country")}`}>{p.country}</td>
            <td className={`px-2 py-1 font-mono whitespace-nowrap ${cellBg(p, "tracking")}`}>{p.tracking ? (<>
              {p.tracking}
              <button type="button" title="Copy tracking" data-testid={`psr-copy-tracking-${i}`}
                onClick={(e) => { e.stopPropagation(); copyText(p.tracking); }} onDoubleClick={(e) => e.stopPropagation()}
                className="ml-1 inline-flex align-middle rounded p-0.5 text-slate-400 hover:bg-indigo-50 hover:text-indigo-600">
                <Copy className="w-3 h-3" /></button></>) : <span className="text-amber-600">—</span>}</td>
            <td className={`px-2 py-1 ${cellBg(p, "invoice_no")}`}>{p.invoice_no || "—"}</td>
            <td className="px-2 py-1 max-w-[320px] break-words" data-testid={`psr-products-${i}`}>
              {p.products.map((x, k) => (<span key={k}>{k > 0 && "; "}
                {x.issue ? <span className="rounded bg-rose-100 px-0.5 font-semibold text-rose-700" title={x.issue} data-testid={`psr-product-issue-${i}-${k}`}>
                  ⚠ {x.medicine || ""} × {x.quantity || ""} <i className="font-normal">({x.issue})</i></span>
                  : `${x.medicine || ""} × ${x.quantity || ""}`}</span>))}</td>
            <td className={`px-2 py-1 text-slate-600 ${cellBg(p, "reason")}`}>{p.reason_detail || p.reason}</td>
            <td className="px-2 py-1 min-w-[180px] max-w-[260px]" onClick={(e) => e.stopPropagation()} data-testid={`psr-note-${i}`}>
              {p.first_id && <ParcelNoteBadge row={{ id: p.first_id, customer_name: p.customer_name, pnote: p.pnote }}
                readOnly={ro} isAdmin={u.role === "admin"} me={u.id} onChanged={onChanged} source="pending_parcels" />}
              {p.pnote?.last && <NoteHover rowId={p.first_id}><span className="ml-1 cursor-help text-[10px] text-slate-600 line-clamp-2"
                style={p.pnote.last.hl ? { backgroundColor: HL[p.pnote.last.hl].bg } : undefined}>
                <b>{p.pnote.last.by_name}:</b> {p.pnote.last.text}</span></NoteHover>}
            </td>
            <td className="px-2 py-1 whitespace-nowrap">{p.first_id && (ro && !p.pquery ? null : p.pquery
              ? <QueryHover rowId={p.first_id}><QueryCell p={p} i={i} /></QueryHover> : <QueryCell p={p} i={i} />)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
  </>);
};

export const AGE_LABEL = { urgent: "Urgent ready", today: "Today", d1: "Yesterday", d2: "2d", d3: "3d", d4_7: "4–7d",
  d8_15: "8–15d", d15p: "15d+", total: "All ages" };
export const psrParams = (f) => (f ? { pbu: 1, age: f.age || "", user: f.user || "" } : {});

/* Service-wise pending window. `filter` (from Pending by User) = {age, user, label} → scope + age limited. */
export const ServiceReportDialog = ({ open, setOpen, filter, onOpenInRegister }) => {
  const [data, setData] = useState(null);
  const [bucket, setBucket] = useState("");
  const [parcels, setParcels] = useState(null);
  const [qf, setQf] = useState("");
  const [rf, setRf] = useState("");
  const fk = JSON.stringify(filter || null);
  useEffect(() => {
    if (!open) return;
    setData(null); setBucket(""); setParcels(null);
    axios.get(`${API}/order-register/pending-service-report`, { params: psrParams(JSON.parse(fk)), headers: getAuthHeader() })
      .then(r => setData(r.data)).catch(e => toast.error(e.response?.data?.detail || "Could not load report"));
  }, [open, fk]);
  const openBucket = async (b, quiet) => {
    setBucket(b); if (!quiet) setParcels(null);
    try {
      setParcels((await axios.get(`${API}/order-register/pending-service-report`,
        { params: { bucket: b, ...psrParams(filter) }, headers: getAuthHeader() })).data.parcels);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load parcels"); }
  };
  const tag = filter ? ` · ${AGE_LABEL[filter.age] || filter.age}${filter.label ? ` · ${filter.label}` : ""}` : "";
  return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-6xl max-sm:max-h-[90vh] max-sm:overflow-y-auto max-sm:overflow-x-hidden" data-testid="psr-dialog">
          <DialogHeader>
            <DialogTitle className="flex flex-wrap items-center gap-2 text-base">
              {bucket && <button type="button" onClick={() => setBucket("")} data-testid="psr-back-btn"
                className="rounded p-1 hover:bg-slate-100"><ArrowLeft className="w-4 h-4" /></button>}
              {bucket ? `${bucket} — pending parcels (oldest first)` : "Pending Parcels — service-wise"}
              {tag && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800" data-testid="psr-filter-tag">{tag.slice(3)}</span>}
              {bucket && <button type="button" onClick={() => openWorklist(bucket, filter)} title="Open this list in a new tab (team worklist)"
                data-testid="psr-open-new-tab-btn"
                className="ml-1 inline-flex items-center gap-0.5 rounded-md border border-indigo-300 px-1.5 py-0.5 text-[11px] font-semibold text-indigo-700 hover:bg-indigo-50">
                <ArrowUpRight className="w-3.5 h-3.5" /> New tab</button>}
            </DialogTitle>
          </DialogHeader>
          {!data ? <div className="py-10 text-center"><Loader2 className="mx-auto w-5 h-5 animate-spin" /></div>
            : !bucket ? (
              <div className="space-y-3">
                <p className="text-xs text-slate-600" data-testid="psr-summary">
                  <b>{data.total_parcels}</b> pending parcels · <b className="text-red-600">{data.total_over_48h}</b> over 48h.
                  ITPS / EMS / CMS to USA are counted separately (USA documentation).
                  {filter && " Only the services and countries allotted to this user are shown."}
                  {data.total_parcels > 0 && <button type="button" onClick={() => openWorklist("ALL", filter)} data-testid="psr-all-new-tab-btn"
                    className="ml-2 inline-flex items-center gap-0.5 rounded-md border border-indigo-300 px-1.5 py-0.5 text-[11px] font-semibold text-indigo-700 hover:bg-indigo-50">
                    <ArrowUpRight className="w-3.5 h-3.5" /> All services in new tab</button>}
                </p>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
                  {data.buckets.map(b => <BucketCard key={b.bucket} b={b} onOpen={openBucket} />)}
                </div>
                {!data.buckets.length && <p className="py-6 text-center text-sm text-slate-500">No pending parcels.</p>}
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="flex flex-wrap items-center gap-2 text-xs text-slate-600 min-w-0" data-testid="psr-bucket-count">
                    {parcels ? (() => { const c = applyQueryFilter(parcels, qf).filter((p) => reasonMatch(p, rf)).length; return `${c} parcel${c === 1 ? "" : "s"}`; })() : "Loading…"}
                    <QueryFilterSelect value={qf} onChange={setQf} parcels={parcels} testId="psr-query-filter" />
                    <ReasonFilterSelect value={rf} onChange={setRf} parcels={parcels} testId="psr-reason-filter" />
                  </span>
                  <button type="button" data-testid="psr-open-register-btn"
                    onClick={() => { onOpenInRegister(bucket); setOpen(false); }}
                    className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-indigo-700">
                    <ExternalLink className="w-3.5 h-3.5" /> {(() => { const u = me(); return u.role !== "admin" && (u.is_query_team || u.order_register_access === "viewer"); })() ? "Open in Register" : "Open in Register to edit"}
                  </button>
                </div>
                {parcels ? <ParcelTable parcels={applyQueryFilter(parcels, qf).filter((p) => reasonMatch(p, rf))} onChanged={() => openBucket(bucket, true)} /> : <div className="py-10 text-center"><Loader2 className="mx-auto w-5 h-5 animate-spin" /></div>}
              </div>
            )}
        </DialogContent>
      </Dialog>
  );
};

export const PendingServiceReport = ({ onOpenInRegister }) => {
  const [can, setCan] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    axios.get(`${API}/order-register/pp/status`, { headers: getAuthHeader() })
      .then(r => setCan(!!r.data.can_report)).catch(() => {});
  }, []);
  // ?psr=1 deep link (Daily Pending Digest notification)
  useEffect(() => {
    const apply = () => { if (new URLSearchParams(window.location.search).get("psr") === "1") setOpen(true); };
    apply();
    window.addEventListener("or-deeplink", apply);
    return () => window.removeEventListener("or-deeplink", apply);
  }, []);
  if (!can) return null;
  return (
    <>
      <button type="button" data-testid="or-pending-service-btn" onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-indigo-400 bg-white px-2.5 py-1 text-[11px] font-semibold text-indigo-700 hover:bg-indigo-50 transition-colors">
        <Layers className="w-3.5 h-3.5" /> Pending by Service
      </button>
      <ServiceReportDialog open={open} setOpen={setOpen} filter={null} onOpenInRegister={onOpenInRegister} />
    </>
  );
};
